import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Tabs } from '../components/Layout';
import { Empty, ErrorState, Kpi, Loading, TextInput } from '../components/ui';
import { api } from '../lib/api';
import { date, daysAgo, money, time, today } from '../lib/format';

type Segment = 'ALL' | 'FNB' | 'ROOMS';

interface DailyReport {
  from: string;
  to: string;
  segment: Segment;
  revenue: {
    bills: number;
    gross: number;
    discount: number;
    net: number;
    gst: { cgst: number; sgst: number; igst: number; total: number };
    roundOff: number;
    collected: number;
    byCategory: Array<{ category: string; department: 'FNB' | 'ROOMS'; quantity: number; amount: number }>;
    byMode: Array<{ mode: string; amount: number; count: number }>;
    byDepartment: Array<{ department: 'FNB' | 'ROOMS'; bills: number; net: number; collected: number }> | null;
    invoices: Array<{
      id: string;
      number: string;
      kind: 'FNB' | 'ROOM';
      issuedAt: string;
      guestName: string;
      mode: string;
      net: number;
      total: number;
    }>;
  };
  rooms: {
    checkIns: number;
    checkOuts: number;
    roomNights: number;
    averageRate: number;
    advances: { amount: number; count: number };
  } | null;
  postedToRooms: { amount: number; count: number } | null;
  voided: { amount: number; count: number };
  expenses: {
    total: number;
    count: number;
    byCategory: Array<{ categoryId: string; category: string; department: string; amount: number; count: number }>;
    byMode: Array<{ mode: string; amount: number; count: number }>;
    list: Array<{
      id: string;
      number: string;
      category: string;
      description: string;
      vendorName: string | null;
      paymentMode: string;
      amount: number;
    }>;
    sharedNotDeducted: { amount: number; count: number } | null;
  };
  procurement: {
    total: number;
    count: number;
    receipts: Array<{
      id: string;
      number: string;
      supplier: string;
      supplierInvoiceNo: string | null;
      receivedAt: string;
      total: number;
    }>;
  } | null;
  result: {
    netRevenue: number;
    expenses: number;
    procurement: number;
    net: number;
    cash: { collected: number; spent: number; net: number };
  };
}

const SEGMENTS: Array<{ id: Segment; label: string }> = [
  { id: 'ALL', label: 'Overall' },
  { id: 'FNB', label: 'F&B' },
  { id: 'ROOMS', label: 'Front desk' },
];

const SEGMENT_TITLES: Record<Segment, string> = {
  ALL: 'Overall daily report',
  FNB: 'F&B daily report',
  ROOMS: 'Front desk (accommodation) daily report',
};

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Credit / debit card',
  ONLINE: 'Online / aggregator',
  ROOM: 'Room charge',
  BANK_TRANSFER: 'Bank transfer',
  OTHER: 'Other',
};

const DEPARTMENT_LABELS: Record<string, string> = {
  FNB: 'F&B',
  ROOMS: 'Front desk',
  FRONT_DESK: 'Front desk',
  SHARED: 'Shared',
};

const modeLabel = (mode: string) => PAYMENT_MODE_LABELS[mode] ?? mode;
const percent = (part: number, whole: number) =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—';
const quantity = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

export function DailyReportPage() {
  const [params, setParams] = useSearchParams();
  const segment = (SEGMENTS.find((s) => s.id === params.get('segment'))?.id ?? 'ALL') as Segment;
  const [day, setDay] = useState(today());

  const report = useQuery({
    queryKey: ['daily-report', segment, day],
    queryFn: () =>
      api.get<DailyReport>(`/api/reports/daily?segment=${segment}&from=${day}&to=${day}`),
    enabled: Boolean(day),
  });

  return (
    <div className="stack">
      <PageHeader
        title={SEGMENT_TITLES[segment]}
        subtitle={date(day)}
        actions={
          <button className="btn no-print" onClick={() => window.print()}>
            Print
          </button>
        }
      />

      <div className="no-print">
        <Tabs
          tabs={SEGMENTS}
          active={segment}
          onChange={(id) => setParams(id === 'ALL' ? {} : { segment: id }, { replace: true })}
        />
      </div>

      <div className="card no-print">
        <div className="card-body row gap-6" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextInput label="Date" type="date" value={day} max={today()} onChange={(e) => setDay(e.target.value)} />
          <button className="btn sm" onClick={() => setDay(today())}>
            Today
          </button>
          <button className="btn sm" onClick={() => setDay(daysAgo(1))}>
            Yesterday
          </button>
        </div>
      </div>

      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : report.data ? (
        <ReportBody data={report.data} />
      ) : null}
    </div>
  );
}

function ReportBody({ data }: { data: DailyReport }) {
  const { revenue, expenses, procurement, result } = data;
  const overall = data.segment === 'ALL';

  return (
    <>
      <div className="kpis">
        <Kpi
          label="Net revenue"
          value={money(result.netRevenue)}
          foot={`${revenue.bills} bill(s) · excluding GST`}
        />
        <Kpi
          label="Collected"
          value={money(revenue.collected)}
          foot={`Including GST ${money(revenue.gst.total)}`}
          tone="blue"
        />
        <Kpi
          label="Expenses"
          value={money(expenses.total)}
          foot={`${expenses.count} entr${expenses.count === 1 ? 'y' : 'ies'}`}
          tone="amber"
        />
        {procurement ? (
          <Kpi
            label="Procurement"
            value={money(procurement.total)}
            foot={`${procurement.count} goods receipt(s)`}
            tone="amber"
          />
        ) : null}
        <Kpi
          label="Net for the day"
          value={money(result.net)}
          foot={procurement ? 'Revenue − expenses − procurement' : 'Revenue − expenses'}
          tone={result.net < 0 ? 'red' : ''}
        />
      </div>

      {overall && revenue.byDepartment ? <DepartmentTable rows={revenue.byDepartment} /> : null}

      <div className="grid-2">
        <CategoryTable data={data} />
        <PaymentTable data={data} />
      </div>

      <div className="grid-2">
        <Calculation data={data} />
        <div className="stack">
          {data.rooms ? <RoomsCard rooms={data.rooms} /> : null}
          <NotesCard data={data} />
        </div>
      </div>

      <div className="grid-2">
        <ExpenseCategoryTable data={data} />
        <ExpenseList data={data} />
      </div>

      {procurement ? <ProcurementTable procurement={procurement} /> : null}

      <BillsTable data={data} />
    </>
  );
}

function DepartmentTable({ rows }: { rows: NonNullable<DailyReport['revenue']['byDepartment']> }) {
  const sum = (pick: (row: (typeof rows)[number]) => number) => rows.reduce((s, r) => s + pick(r), 0);
  return (
    <div className="card">
      <div className="card-head">
        <h3>By department</h3>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <thead>
            <tr>
              <th>Department</th>
              <th className="num">Bills</th>
              <th className="num">Net revenue</th>
              <th className="num">Collected (incl. GST)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.department}>
                <td>{row.department === 'FNB' ? 'F&B (restaurant counters)' : 'Front desk (accommodation)'}</td>
                <td className="num">{row.bills}</td>
                <td className="num">{money(row.net)}</td>
                <td className="num">{money(row.collected)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="num">{sum((r) => r.bills)}</td>
              <td className="num">{money(sum((r) => r.net))}</td>
              <td className="num">{money(sum((r) => r.collected))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function CategoryTable({ data }: { data: DailyReport }) {
  const { revenue } = data;
  const overall = data.segment === 'ALL';
  return (
    <div className="card">
      <div className="card-head">
        <h3>Revenue by category</h3>
      </div>
      {revenue.byCategory.length === 0 ? (
        <Empty message="No paid bills on this day." />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Category</th>
                <th className="num">Qty</th>
                <th className="num">Amount</th>
                <th className="num">Share</th>
              </tr>
            </thead>
            <tbody>
              {revenue.byCategory.map((row) => (
                <tr key={row.category}>
                  <td>
                    {row.category}
                    {overall ? <span className="xs muted"> · {DEPARTMENT_LABELS[row.department]}</span> : null}
                  </td>
                  <td className="num">{quantity(row.quantity)}</td>
                  <td className="num">{money(row.amount)}</td>
                  <td className="num">{percent(row.amount, revenue.net)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Net revenue (excluding GST)</td>
                <td className="num">{money(revenue.net)}</td>
                <td className="num">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentTable({ data }: { data: DailyReport }) {
  const { revenue } = data;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Payments received</h3>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <thead>
            <tr>
              <th>Payment type</th>
              <th className="num">Bills</th>
              <th className="num">Amount</th>
              <th className="num">Share</th>
            </tr>
          </thead>
          <tbody>
            {revenue.byMode.map((row) => (
              <tr key={row.mode}>
                <td>{modeLabel(row.mode)}</td>
                <td className="num">{row.count}</td>
                <td className="num">{money(row.amount)}</td>
                <td className="num">{percent(row.amount, revenue.collected)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total collected</td>
              <td className="num">{revenue.bills}</td>
              <td className="num">{money(revenue.collected)}</td>
              <td className="num">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/** The day as a statement: from what was charged, to what it leaves. */
function Calculation({ data }: { data: DailyReport }) {
  const { revenue, result, procurement } = data;
  const line = (label: string, value: number, opts: { minus?: boolean; strong?: boolean } = {}) => (
    <tr className={opts.strong ? 'b' : ''}>
      <td>{label}</td>
      <td className="num">{opts.minus && value !== 0 ? `− ${money(value)}` : money(value)}</td>
    </tr>
  );
  return (
    <div className="card">
      <div className="card-head">
        <h3>Revenue calculation</h3>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <tbody>
            {line('Gross sales', revenue.gross)}
            {line('Less: discounts', revenue.discount, { minus: true })}
            {line('Net sales (taxable value)', revenue.net, { strong: true })}
            {line('Add: CGST', revenue.gst.cgst)}
            {line('Add: SGST', revenue.gst.sgst)}
            {revenue.gst.igst > 0 ? line('Add: IGST', revenue.gst.igst) : null}
            {line('Round off', revenue.roundOff)}
            {line('Total collected', revenue.collected, { strong: true })}
          </tbody>
        </table>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <tbody>
            {line('Net sales', result.netRevenue)}
            {line('Less: expenses', result.expenses, { minus: true })}
            {procurement ? line('Less: procurement', result.procurement, { minus: true }) : null}
            {line('Net for the day', result.net, { strong: true })}
          </tbody>
        </table>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <tbody>
            {line('Cash collected', result.cash.collected)}
            {line('Less: expenses paid in cash', result.cash.spent, { minus: true })}
            {line('Cash in hand from the day', result.cash.net, { strong: true })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RoomsCard({ rooms }: { rooms: NonNullable<DailyReport['rooms']> }) {
  const row = (label: string, value: string) => (
    <tr>
      <td>{label}</td>
      <td className="num">{value}</td>
    </tr>
  );
  return (
    <div className="card">
      <div className="card-head">
        <h3>Rooms</h3>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <tbody>
            {row('Check-ins', String(rooms.checkIns))}
            {row('Check-outs (bills settled)', String(rooms.checkOuts))}
            {row('Room nights billed', quantity(rooms.roomNights))}
            {row('Average room rate', money(rooms.averageRate))}
            {row(
              `Advances taken at check-in (${rooms.advances.count})`,
              money(rooms.advances.amount),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** What the totals above leave out, said where somebody reconciling will look. */
function NotesCard({ data }: { data: DailyReport }) {
  const notes: string[] = [];
  if (data.postedToRooms && data.postedToRooms.count > 0) {
    notes.push(
      `${data.postedToRooms.count} room-service bill(s) worth ${money(data.postedToRooms.amount)} were posted to guest rooms. They are collected at check-out and counted in the front desk report then.`,
    );
  }
  if (data.expenses.sharedNotDeducted && data.expenses.sharedNotDeducted.count > 0) {
    notes.push(
      `${money(data.expenses.sharedNotDeducted.amount)} of shared expenses (${data.expenses.sharedNotDeducted.count}) is not deducted here - it is deducted in the overall report.`,
    );
  }
  if (data.voided.count > 0) {
    notes.push(`${data.voided.count} bill(s) worth ${money(data.voided.amount)} were voided and are left out.`);
  }
  if (data.rooms && data.rooms.advances.count > 0) {
    notes.push(
      'Advances are recorded without a payment type, so they appear in revenue only when the guest checks out.',
    );
  }
  if (data.procurement) {
    notes.push('Procurement is stock received from suppliers; it is not part of the cash figures.');
  }
  if (notes.length === 0) return null;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Notes</h3>
      </div>
      <div className="card-body">
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ExpenseCategoryTable({ data }: { data: DailyReport }) {
  const { expenses } = data;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Expenses by category</h3>
      </div>
      {expenses.byCategory.length === 0 ? (
        <Empty message="No expenses under this report's categories on this day." />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Category</th>
                <th className="num">Entries</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {expenses.byCategory.map((row) => (
                <tr key={row.categoryId}>
                  <td>
                    {row.category}
                    {data.segment === 'ALL' ? (
                      <span className="xs muted"> · {DEPARTMENT_LABELS[row.department]}</span>
                    ) : null}
                  </td>
                  <td className="num">{row.count}</td>
                  <td className="num">{money(row.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total expenses</td>
                <td className="num">{expenses.count}</td>
                <td className="num">{money(expenses.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function ExpenseList({ data }: { data: DailyReport }) {
  const { expenses } = data;
  if (expenses.list.length === 0) return <div />;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Expense entries</h3>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <thead>
            <tr>
              <th>No.</th>
              <th>Category</th>
              <th>Description</th>
              <th>Paid by</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {expenses.list.map((row) => (
              <tr key={row.id}>
                <td className="mono xs">{row.number}</td>
                <td>{row.category}</td>
                <td>
                  {row.description}
                  {row.vendorName ? <span className="xs muted"> · {row.vendorName}</span> : null}
                </td>
                <td>{modeLabel(row.paymentMode)}</td>
                <td className="num">{money(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProcurementTable({ procurement }: { procurement: NonNullable<DailyReport['procurement']> }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Procurement (goods received)</h3>
      </div>
      {procurement.receipts.length === 0 ? (
        <Empty message="No supplier deliveries were received on this day." />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Supplier</th>
                <th>Supplier invoice</th>
                <th>Time</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {procurement.receipts.map((row) => (
                <tr key={row.id}>
                  <td className="mono xs">{row.number}</td>
                  <td>{row.supplier}</td>
                  <td>{row.supplierInvoiceNo ?? '—'}</td>
                  <td>{time(row.receivedAt)}</td>
                  <td className="num">{money(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Total procurement</td>
                <td className="num">{money(procurement.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function BillsTable({ data }: { data: DailyReport }) {
  const { invoices } = data.revenue;
  if (invoices.length === 0) return null;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Bills ({invoices.length})</h3>
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-compact">
          <thead>
            <tr>
              <th>Bill</th>
              <th>Time</th>
              <th>Guest</th>
              {data.segment === 'ALL' ? <th>Department</th> : null}
              <th>Paid by</th>
              <th className="num">Net</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((row) => (
              <tr key={row.id}>
                <td className="mono xs">{row.number}</td>
                <td>{time(row.issuedAt)}</td>
                <td>{row.guestName}</td>
                {data.segment === 'ALL' ? (
                  <td>{row.kind === 'FNB' ? 'F&B' : 'Front desk'}</td>
                ) : null}
                <td>{modeLabel(row.mode)}</td>
                <td className="num">{money(row.net)}</td>
                <td className="num">{money(row.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
